import { isEmptyInput, isNullOrUndefined } from '@adaptiva/core/common';
import { MenuItem } from '@adaptiva/core/menus';
import { EditorInput } from '@adaptiva/data-service';
import { BlacklistProperties, HousekeepingProperties, IMetadataObject, MetadataMenuController, MetadataObject, MetadataPropertyCopyService, MetadataSerialization, RelationshipsProperties } from '@adaptiva/metadata/common';
import { EventEmitter, Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { BaseMetadataEditorComponent } from '../../base-metadata-editor';
import { CopyPropertiesOverlayComponent } from '../overlays';

@Injectable()
export class AuthoringToolbarAuthoringMenucontroller<T extends MetadataObject> extends MetadataMenuController<T> {

	public readonly saveMetadataObjectMenuName: string = 'Save';

	public readonly saveMetadataObjectAndCompleteMenuName: string = 'Save and Submit for Approval';

	public readonly openParentObjectMenuName: string = 'Open Parent Object';

	public readonly duplicateMenuName: string = 'Duplicate Object';

	public readonly openPreviousObjectMenuName: string = 'Open Previous Object';

	public readonly openNextObjectMenuName: string = 'Open Next Object';

	public readonly copyPropertiesMenuName: string = 'Copy Properties';

	public readonly pastePropertiesMenuName: string = 'Paste Properties'; //

	public isSaveButtonEnabled: BehaviorSubject<boolean>;

	public propertiesPasted!: EventEmitter<T>;

	public saveAndComplete!: EventEmitter<boolean>;

	protected copyService: MetadataPropertyCopyService = inject(MetadataPropertyCopyService);

	private _onCurrentObject: BehaviorSubject<MetadataObject>;

	private _onCurrentObjectSubscription: Subscription;

	public override generate(_meta: T): Observable<MenuItem<T>[]> {
		if (!(_meta instanceof MetadataObject))
			_meta = this.summarize.generate<T>((_meta as IMetadataObject).javaClassName, _meta);

		return of([
			this.getCheckInCheckOutMenuItem(_meta),
			this.getSaveMetadataObjectMenuItem(_meta),
			this.getSaveMetadataObjectAndCompleteMenuItem(_meta),
			this.getSubmitForApprovalMenuItem(_meta),
			this.getDeleteMenuItem(_meta),
			...this.getParentMenus(_meta),
			this.getDuplicateMenuItem(_meta),
			...this.getSequentialMenuItems(_meta),
			this.getCopyPropertiesMenuItem(_meta),
			this.getPastePropertiesMenuItem(_meta),
		]);
	}

	// #region Initialization

	public setOnCurrentObject(_subject: BehaviorSubject<MetadataObject>): void {
		if (!_subject)
			return;

		this._onCurrentObject = _subject;
		this._onCurrentObjectSubscription?.unsubscribe?.();
		this._onCurrentObjectSubscription = this._onCurrentObject.pipe(
			this.takeUntilDestroyed()
		).subscribe({
			next: () => this.clear()
		});
	}

	// #region Menu Enablers

	protected override enableCheckInCheckOut(_item: MenuItem<T>): void {
		const _request: Observable<boolean> = _item.data.isCheckedOut()
			? this.authoringService.isCheckInAllowed([_item.data])
			: this.authoringService.isCheckOutAllowed([_item.data.objectId]);

		this.setAllowedStateIfButtonEnabled(_request, _item, true);
	}

	protected override enableSubmitForApproval(_item: MenuItem<T>): void {
		this.setAllowedStateIfButtonEnabled(this.authoringService.isCompleteAllowed(_item.data), _item, true);
	}

	// #region Menu Getters

	protected getParentMenus(_object: T): MenuItem<T>[] {
		const _menus: MenuItem<T>[] = [];

		if (!isEmptyInput(this._parentId(_object), true))
			_menus.push(this.getOpenParentObjectMenuItem(_object));

		return _menus;
	}

	protected getSequentialMenuItems(_object: T): MenuItem<T>[] {
		const _menus: MenuItem<T>[] = [];

		if (this._hasRelationship(_object, 'Supersedes'))
			_menus.push(this.getOpenPreviousObjectMenuItem(_object));

		if (this._hasRelationship(_object, 'SupersededBy'))
			_menus.push(this.getOpenNextObjectMenuItem(_object));

		return _menus;
	}

	// #region Save Metadata Object

	protected getSaveMetadataObjectMenuItem(_object: T): MenuItem<T> {
		const _menuItem: MenuItem<T> = this.createItem({
			id: this.hashItem(this.saveMetadataObjectMenuName, _object),
			name: this.saveMetadataObjectMenuName,
			data: _object,
			onSelect: this.saveMetadataObject.bind(this)
		});

		this.enableSaveMetadataObjectMenuItem(_menuItem);

		return _menuItem;
	}

	protected enableSaveMetadataObjectMenuItem(_menu: MenuItem<T>): void {
		this.setAllowedStateIfButtonEnabled(this.authoringService.canEdit(_menu.data), _menu);
	}

	protected saveMetadataObject(): void {
		this.globalEditorService.editorInputChange.pipe(
			take(1),
			map((_input: EditorInput<T>) => {
				return this.globalEditorService.getEditorComponent(_input.editorId) as BaseMetadataEditorComponent<T>;
			}),
			take(1)
		).subscribe({
			next: (_editorComponent: BaseMetadataEditorComponent<T>) => {
				_editorComponent.saveMetadataObject();
			}
		});
	}

	// #region Save Metadata Object and Complete

	protected getSaveMetadataObjectAndCompleteMenuItem(_object: T): MenuItem<T> {
		const _menuItem: MenuItem<T> = this.createItem({
			id: this.hashItem(this.saveMetadataObjectAndCompleteMenuName, _object),
			name: this.saveMetadataObjectAndCompleteMenuName,
			data: _object,
			onSelect: this.saveMetadataObjectAndComplete.bind(this)
		});

		this.enableSaveMetadataObjectMenuItem(_menuItem);

		return _menuItem;
	}

	protected saveMetadataObjectAndComplete(): void {
		this.saveAndComplete.emit(true);
		this.saveMetadataObject();
	}

	// #region Open Parent Object

	protected getOpenParentObjectMenuItem(_object: T): MenuItem<T> {
		const _menuItem: MenuItem<T> = this.createItem({
			id: this.hashItem(this.openParentObjectMenuName, _object),
			name: this.openParentObjectMenuName,
			data: _object,
			onSelect: this.openParentObject.bind(this)
		});


		return _menuItem;
	}

	protected openParentObject(_menu: MenuItem<T>): void {
		const _object: T = _menu.data;
		const _parentObjectId: number = this._parentId(_object);

		if (_parentObjectId) {
			const _parentClassName: string = MetadataSerialization.getParentClassName(_object);
			const _data: Partial<T> = {};

			_data.objectId = _parentObjectId;
			const _editorInput: EditorInput<T> = new EditorInput<T>(_parentClassName, _data);

			this.globalEditorService.openEditorWithChanges(_editorInput);
		}
	}

	// #region Duplicate

	protected getDuplicateMenuItem(_object: T): MenuItem<T> {
		const _menuItem: MenuItem<T> = this.createItem({
			id: this.hashItem(this.duplicateMenuName, _object),
			name: this.duplicateMenuName,
			data: _object,
			onSelect: this.duplicate.bind(this)
		});

		return _menuItem;
	}

	protected duplicate(_menu: MenuItem<T>): void {
		const _object: T = _menu.data;
		const _clone: T = _object.duplicate() as T;
		const _parameters: Params = this.routingService.getRouteParams();

		_clone.authoringProperties.Housekeeping = MetadataSerialization.construct(
			this.summarize.getClass(_object.javaClassName),
			HousekeepingProperties
		);
		_clone.authoringProperties.Blacklist = MetadataSerialization.construct(
			this.summarize.getClass(_object.javaClassName),
			BlacklistProperties
		);

		const _editorInput: EditorInput<T> = new EditorInput<T>(_object.javaClassName, _clone, _parameters);

		this.globalEditorService.openEditorWithChanges(_editorInput);
	}

	// #region Open Previous Object

	protected getOpenPreviousObjectMenuItem(_object: T): MenuItem<T> {
		const _menuItem: MenuItem<T> = this.createItem({
			id: this.hashItem(this.openPreviousObjectMenuName, _object),
			name: this.openPreviousObjectMenuName,
			data: _object,
			onSelect: this.previousObject.bind(this)
		});

		return _menuItem;
	}

	protected previousObject(_menu: MenuItem<T>): void {
		this._navigate(_menu.data, 'Supersedes');
	}

	// #region Open Next Object

	protected getOpenNextObjectMenuItem(_object: T): MenuItem<T> {
		const _menuItem: MenuItem<T> = this.createItem({
			id: this.hashItem(this.openNextObjectMenuName, _object),
			name: this.openNextObjectMenuName,
			data: _object,
			onSelect: this.nextObject.bind(this)
		});

		return _menuItem;
	}

	protected nextObject(_menu: MenuItem<T>): void {
		this._navigate(_menu.data, 'SupersededBy');
	}

	// #region Copy Properties

	protected getCopyPropertiesMenuItem(_object: T): MenuItem<T> {
		const _menuItem: MenuItem<T> = this.createItem({
			id: this.hashItem(this.copyPropertiesMenuName, _object),
			name: this.copyPropertiesMenuName,
			data: _object,
			onSelect: this.copyProperties.bind(this)
		});

		return _menuItem;
	}

	protected copyProperties(_menu: MenuItem<T>): void {
		this.overlays.create(
			CopyPropertiesOverlayComponent<T>,
			_menu.data
		).subscribe();
	}

	// #region Paste Properties

	protected getPastePropertiesMenuItem(_object: T): MenuItem<T> {
		const _menuName: string = this.pastePropertiesDisplayName();
		const _itemId: string = this.hashItem(this.pastePropertiesMenuName, _object);
		let _menuItem: MenuItem<T> = this.getItem(_itemId, _object);

		if (isNullOrUndefined(_menuItem)) {
			_menuItem = this.createItem({
				id: _itemId,
				name: _menuName,
				data: _object,
				onSelect: this.pasteProperties.bind(this)
			});
		}
		else 
			_menuItem.name = _menuName;
		

		this.enablePastePropertiesMenuItem(_menuItem);

		return _menuItem;
	}

	protected enablePastePropertiesMenuItem(_menu: MenuItem<T>): void {
		const _object: T = _menu.data;

		this.authoringService.canEdit(_object).pipe(take(1)).subscribe({
			next: (_canEdit: boolean) => {
				const isCompatible: boolean = this.copyService.isCompatible(_object);

				_menu.isDisabled.next(!_canEdit || !isCompatible);
			}
		});
	}

	protected pasteProperties(_menu: MenuItem<T>): void {
		if (isNullOrUndefined(this.propertiesPasted))
			throw new Error('No properties pasted emitter set on AuthoringToolbarAuthoringMenuController');

		const _newObject: T = _menu.data;

		this.copyService.pasteProperties(_newObject);
		this.propertiesPasted.emit(_newObject);
	}

	// #region Helpers

	protected pastePropertiesDisplayName(): string {
		const _pasteName: string = isEmptyInput(this.copyService.copiedName?.value) ? '' : ` from ${this.copyService.copiedName.value}`;

		return `${this.pastePropertiesMenuName}${_pasteName}`;
	}

	protected setAllowedStateIfButtonEnabled(_request: Observable<boolean>, _item: MenuItem<T>, _invert: boolean = false): void {
		this.setAllowedState(
			this.isSaveButtonEnabled.pipe(
				switchMap((_enabled: boolean) => {
					if (_invert)
						_enabled = !_enabled;

					return _enabled ? _request : of(false);
				}),
			),
			_item
		);
	}

	private _hasRelationship(_object: T, _relationship: 'SupersededBy' | 'Supersedes'): boolean {
		const _relationships: int[] = MetadataSerialization.getPropertyValue<int[], RelationshipsProperties>(
			_object, 'Relationships', _relationship
		);

		return !isEmptyInput(_relationships);
	}

	private _navigate(_object: T, _property: keyof RelationshipsProperties & 'SupersededBy' | 'Supersedes'): void {
		const _navigationIds: int[] = MetadataSerialization.getPropertyValue<int[], RelationshipsProperties>(
			_object, 'Relationships', _property
		);

		if (_navigationIds?.length) {
			const [_nextId]: long[] = _navigationIds;
			const _data: Partial<T> = {};

			_data.objectId = _nextId;
			const _editorInput: EditorInput<T> = new EditorInput<T>(_object.javaClassName, _data);

			this.globalEditorService.openEditorWithChanges(_editorInput);
		}
	}

	private _parentId(_object: T): long {
		return MetadataSerialization.getPropertyValue<int, RelationshipsProperties>(
			_object, 'Relationships', 'Parent'
		);
	}

}
